import React from 'react';
import { Paper, Table, TableHead, TableBody, TableRow, TableCell, Grid } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { filter, find, get } from 'lodash';

import { CommercialReimbursement, OtherIncomeItem } from 'types/underwriting/income-approach';
import { formatCurrencyFloat, formatCurrencyInt, formatPercentageString } from 'shared/utils/numberFormatters';
import { divide } from 'shared/utils/numberOperations';
import { expenseForecastFormHelpers } from '../expenses/expense-forecast/expenseForecastFormConfig';
import {
  BASIS_TYPES,
  DEFAULT_EXPENSES_KEYS,
  DEFAULT_EXPENSES_TITLES,
  EXPENSE_PERIOD_KEYS,
} from 'shared/constants/expenses';
import { hasCommercial, hasResidential } from '../helpers';
import { EntryMode, ExpenseHistory } from 'types/underwriting/income-approach/expenses';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';

type ProFormaTableProps = {
  underwritingData: UnderwritingOutput;
};

const totalRowSX = {
  '& .MuiTableCell-root': {
    fontWeight: 'bold',
    backgroundColor: 'rgb(245, 245, 245)',
    color: '#666',
  },
};
const headerSX = {
  '& .MuiTableCell-root': {
    fontWeight: 'bold',
    backgroundColor: '#ffffff',
    color: 'black',
  },
};
const alignRightSX = {
  minWidth: '1200px',
  '& .MuiTableCell-root': {
    textAlign: 'left',
    padding: 0.5,
    backgroundColor: 'rgb(245, 245, 245)',
    color: '#666',
  },
};

const ProFormaTable: React.FC<ProFormaTableProps> = observer(({ underwritingData }) => {
  const expenseHistoryEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseHistoryEntryMode'
  );
  const expenseForecastEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseForecastEntryMode'
  );

  const expenseHistory = get(underwritingData, 'incomeApproach.expenses.expenseHistory', []);
  const expenseForecasts = get(underwritingData, 'incomeApproach.expenses.expenseForecasts', []);
  const expenseCategories = get(underwritingData, 'incomeApproach.expenses.expenseCategories', []);

  const isSummarizedExpense = expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE;
  const isSummarizedForecast = expenseForecastEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE;

  const expenseCategoriesToFilter = !isSummarizedExpense
    ? expenseCategories
    : filter(expenseCategories, (category) => Object.values(DEFAULT_EXPENSES_KEYS).includes(category.key));

  const filteredExpenseCategories = filter(
    expenseCategoriesToFilter,
    (category) => category.key !== DEFAULT_EXPENSES_KEYS.realEstateTaxes
  );
  const otherIncome = get(underwritingData, 'incomeApproach.otherIncome', []);
  const taxLiability = incomeApproachCalculations.calculateTaxLiability(underwritingData);
  const propertyType = get(underwritingData, 'property.propertyType', 'mixed-use');
  const grossBuildingArea = get(underwritingData, 'property.grossBuildingArea', 0);
  const residentialUnits = get(underwritingData, 'property.residentialUnits', 0);
  const residentialVCLossPercentage = get(underwritingData, 'incomeApproach.residential.vcLossPercentage', 0);
  const commercialVCLossPercentage = get(underwritingData, 'incomeApproach.commercial.vcLossPercentage', 0);
  const commercialReimbursements = get(underwritingData, 'incomeApproach.commercial.reimbursements', []);

  const expenseHistoryProForma = find(
    expenseHistory,
    (expense: ExpenseHistory) => expense.period === EXPENSE_PERIOD_KEYS.PROJECTION
  );
  const borrowerExpenses = filter(
    expenseHistoryProForma?.expenses || [],
    (expense) => expense.category !== DEFAULT_EXPENSES_TITLES.realEstateTaxes
  );
  const borrowerTaxes = find(
    expenseHistoryProForma?.expenses || [],
    (expense) => expense.category === DEFAULT_EXPENSES_TITLES.realEstateTaxes
  );
  const borrowerTaxLiability = get(borrowerTaxes, 'value', 0);

  const borrowerResIncome = incomeApproachCalculations.calculateResidentialIncomeTotal(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterResIncome = incomeApproachCalculations.calculateResidentialIncomeTotal(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerResVCLoss = incomeApproachCalculations.calculateResidentialVCLoss(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterResVCLoss = incomeApproachCalculations.calculateResidentialVCLoss(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );
  const borrowerComVCLoss = incomeApproachCalculations.calculateCommercialVCLoss(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterComVCLoss = incomeApproachCalculations.calculateCommercialVCLoss(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerComIncome = incomeApproachCalculations.calculateCommercialIncomeTotal(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterComIncome = incomeApproachCalculations.calculateCommercialIncomeTotal(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerPGI = incomeApproachCalculations.calculatePotentialGrossIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterPGI = incomeApproachCalculations.calculatePotentialGrossIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerEGI = incomeApproachCalculations.calculateEffectiveGrossIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterEGI = incomeApproachCalculations.calculateEffectiveGrossIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerNOI = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterNOI = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );
  const borrowerOpex = incomeApproachCalculations.calculateOperatingExpenseRatio(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterOpex = incomeApproachCalculations.calculateOperatingExpenseRatio(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const borrowerTotalOpex = incomeApproachCalculations.calculateTotalOperatingExpenses(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterTotalOpex = incomeApproachCalculations.calculateTotalOperatingExpenses(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );
  const borrowerTotalOpexExcludingTaxes =
    incomeApproachCalculations.calculateTotalOperatingExpensesExcludingRealEstateTaxes(
      underwritingData,
      INCOME_APPROACH_BASIS_TYPES.borrower
    );
  const underwriterTotalOpexExcludingTaxes =
    incomeApproachCalculations.calculateTotalOperatingExpensesExcludingRealEstateTaxes(
      underwritingData,
      INCOME_APPROACH_BASIS_TYPES.projected
    );

  const showResidential = hasResidential(propertyType);
  const showCommercial = hasCommercial(propertyType);

  return (
    <Paper key="pro-forma-panel" sx={{ pl: 2, mb: 6 }}>
      <Grid container spacing={2} sx={{ pt: 2 }} justifyContent="flex-start">
        <Table sx={alignRightSX}>
          <TableHead>
            <TableRow sx={headerSX}>
              <TableCell>Item</TableCell>
              <TableCell>Borrower</TableCell>
              <TableCell>PSF</TableCell>
              <TableCell>Per Unit</TableCell>
              <TableCell sx={{ width: '32px' }}></TableCell>
              <TableCell>Underwriter</TableCell>
              <TableCell>PSF</TableCell>
              <TableCell>Per Unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showResidential && (
              <TableRow>
                <TableCell>Potential Residential Income</TableCell>
                <TableCell>{formatCurrencyInt(borrowerResIncome)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(borrowerResIncome, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(borrowerResIncome, residentialUnits))}</TableCell>
                <TableCell></TableCell>
                <TableCell>{formatCurrencyInt(underwriterResIncome)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(underwriterResIncome, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(underwriterResIncome, residentialUnits))}</TableCell>
              </TableRow>
            )}
            {showCommercial && (
              <TableRow>
                <TableCell>Potential Commercial Income</TableCell>
                <TableCell>{formatCurrencyInt(borrowerComIncome)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(borrowerComIncome, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(borrowerComIncome, residentialUnits))}</TableCell>
                <TableCell></TableCell>
                <TableCell>{formatCurrencyInt(underwriterComIncome)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(underwriterComIncome, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(underwriterComIncome, residentialUnits))}</TableCell>
              </TableRow>
            )}
            {otherIncome.map((item: OtherIncomeItem) => {
              const incomeAmount = item.amount || 0;
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{formatCurrencyInt(incomeAmount)}</TableCell>
                  <TableCell>{formatCurrencyFloat(divide(incomeAmount, grossBuildingArea))}</TableCell>
                  <TableCell>{formatCurrencyInt(divide(incomeAmount, residentialUnits))}</TableCell>
                  <TableCell></TableCell>
                  <TableCell>{formatCurrencyInt(incomeAmount)}</TableCell>
                  <TableCell>{formatCurrencyFloat(divide(incomeAmount, grossBuildingArea))}</TableCell>
                  <TableCell>{formatCurrencyInt(divide(incomeAmount, residentialUnits))}</TableCell>
                </TableRow>
              );
            })}
            {commercialReimbursements.map((commercialReimbursement: CommercialReimbursement) => {
              return (
                <TableRow key={commercialReimbursement.key}>
                  <TableCell>{`Potential ${commercialReimbursement.name} Reimbursement`}</TableCell>
                  <TableCell>{formatCurrencyInt(commercialReimbursement.grossAnnualValue || 0)}</TableCell>
                  <TableCell>
                    {formatCurrencyFloat(divide(commercialReimbursement.grossAnnualValue || 0, grossBuildingArea))}
                  </TableCell>
                  <TableCell>
                    {formatCurrencyInt(divide(commercialReimbursement.grossAnnualValue || 0, residentialUnits))}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>{formatCurrencyInt(commercialReimbursement.grossAnnualValue || 0)}</TableCell>
                  <TableCell>
                    {formatCurrencyFloat(divide(commercialReimbursement.grossAnnualValue || 0, grossBuildingArea))}
                  </TableCell>
                  <TableCell>
                    {formatCurrencyInt(divide(commercialReimbursement.grossAnnualValue || 0, residentialUnits))}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={totalRowSX}>
              <TableCell>Potential Gross Income</TableCell>
              <TableCell>{formatCurrencyInt(borrowerPGI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(borrowerPGI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(borrowerPGI, residentialUnits))}</TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(underwriterPGI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(underwriterPGI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(underwriterPGI, residentialUnits))}</TableCell>
            </TableRow>
            {showResidential && (
              <TableRow>
                <TableCell>
                  Less Residential V/C Loss @ {formatPercentageString(residentialVCLossPercentage, 2)}
                </TableCell>
                <TableCell>{formatCurrencyInt(-1 * borrowerResVCLoss)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(-1 * borrowerResVCLoss, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(-1 * borrowerResVCLoss, residentialUnits))}</TableCell>
                <TableCell></TableCell>
                <TableCell>{formatCurrencyInt(-1 * underwriterResVCLoss)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(-1 * underwriterResVCLoss, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(-1 * underwriterResVCLoss, residentialUnits))}</TableCell>
              </TableRow>
            )}
            {showCommercial && (
              <TableRow>
                <TableCell>
                  Less Commercial V/C Loss @ {formatPercentageString(commercialVCLossPercentage, 2)}
                </TableCell>
                <TableCell>{formatCurrencyInt(-1 * borrowerComVCLoss)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(-1 * borrowerComVCLoss, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(-1 * borrowerComVCLoss, residentialUnits))}</TableCell>
                <TableCell></TableCell>
                <TableCell>{formatCurrencyInt(-1 * underwriterComVCLoss)}</TableCell>
                <TableCell>{formatCurrencyFloat(divide(-1 * underwriterComVCLoss, grossBuildingArea))}</TableCell>
                <TableCell>{formatCurrencyInt(divide(-1 * underwriterComVCLoss, residentialUnits))}</TableCell>
              </TableRow>
            )}
            {otherIncome.map((item: OtherIncomeItem) => {
              const amount = item.amount || 0;
              const vcLossPercentage = item.vcLossPercentage || 0;
              const lossAmount = divide(amount * vcLossPercentage, 100);
              return (
                <TableRow key={`loss-${item.id}`}>
                  <TableCell>
                    Less {item.name} @ {formatPercentageString(vcLossPercentage, 2)}
                  </TableCell>
                  <TableCell>{formatCurrencyInt(-1 * lossAmount)}</TableCell>
                  <TableCell>{formatCurrencyFloat(divide(-1 * lossAmount, grossBuildingArea))}</TableCell>
                  <TableCell>{formatCurrencyInt(divide(-1 * lossAmount, residentialUnits))}</TableCell>
                  <TableCell></TableCell>
                  <TableCell>{formatCurrencyInt(-1 * lossAmount)}</TableCell>
                  <TableCell>{formatCurrencyFloat(divide(-1 * lossAmount, grossBuildingArea))}</TableCell>
                  <TableCell>{formatCurrencyInt(divide(-1 * lossAmount, residentialUnits))}</TableCell>
                </TableRow>
              );
            })}
            {commercialReimbursements.map((commercialReimbursement: CommercialReimbursement) => {
              return (
                <TableRow key={commercialReimbursement.key}>
                  <TableCell>{`Less ${commercialReimbursement.name} Reimbursement @ ${commercialReimbursement.vcLoss} %`}</TableCell>
                  <TableCell>{formatCurrencyInt(-1 * (commercialReimbursement.grossVCLoss || 0))}</TableCell>
                  <TableCell>
                    {formatCurrencyFloat(-1 * divide(commercialReimbursement.grossVCLoss || 0, grossBuildingArea))}
                  </TableCell>
                  <TableCell>
                    {formatCurrencyInt(-1 * divide(commercialReimbursement.grossVCLoss || 0, residentialUnits))}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>{formatCurrencyInt(-1 * (commercialReimbursement.grossVCLoss || 0))}</TableCell>
                  <TableCell>
                    {formatCurrencyFloat(-1 * divide(commercialReimbursement.grossVCLoss || 0, grossBuildingArea))}
                  </TableCell>
                  <TableCell>
                    {formatCurrencyInt(-1 * divide(commercialReimbursement.grossVCLoss || 0, residentialUnits))}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={totalRowSX}>
              <TableCell>Effective Gross Income</TableCell>
              <TableCell>{formatCurrencyInt(borrowerEGI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(borrowerEGI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(borrowerEGI, residentialUnits))}</TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(underwriterEGI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(underwriterEGI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(underwriterEGI, residentialUnits))}</TableCell>
            </TableRow>
            <TableRow sx={totalRowSX}>
              <TableCell>Operating Expenses</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Real Estate Taxes</TableCell>
              <TableCell>{isSummarizedExpense ? '-' : formatCurrencyInt(borrowerTaxLiability)}</TableCell>
              <TableCell>
                {isSummarizedExpense ? '-' : formatCurrencyFloat(divide(borrowerTaxLiability, grossBuildingArea))}
              </TableCell>
              <TableCell>
                {isSummarizedExpense ? '-' : formatCurrencyInt(divide(borrowerTaxLiability, residentialUnits))}
              </TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(taxLiability)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(taxLiability, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(taxLiability, residentialUnits))}</TableCell>
            </TableRow>
            {filteredExpenseCategories.map((expenseCategory) => {
              const { key, category } = expenseCategory;
              const expenseForecast = find(expenseForecasts, (expenseForecast) => expenseForecast.key === key) || {};
              const { concludedValue = 0, basis = BASIS_TYPES.psf } = expenseForecast;
              const borrowerForecast = find(borrowerExpenses, (expense) => expense.category === category);
              const values = expenseForecastFormHelpers.getConcludedValueByBasis(
                concludedValue,
                basis,
                grossBuildingArea,
                residentialUnits,
                underwriterEGI
              );
              const borrowerValue = borrowerForecast?.value || 0;
              return (
                <TableRow>
                  <TableCell>{category}</TableCell>
                  <TableCell>{isSummarizedExpense ? '-' : formatCurrencyInt(borrowerValue)}</TableCell>
                  <TableCell>
                    {isSummarizedExpense ? '-' : formatCurrencyFloat(divide(borrowerValue, grossBuildingArea))}
                  </TableCell>
                  <TableCell>
                    {isSummarizedExpense ? '-' : formatCurrencyInt(divide(borrowerValue, residentialUnits))}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>{isSummarizedForecast ? '-' : formatCurrencyInt(values.total)}</TableCell>
                  <TableCell>{isSummarizedForecast ? '-' : formatCurrencyFloat(values.psf)}</TableCell>
                  <TableCell>{isSummarizedForecast ? '-' : formatCurrencyInt(values.perUnit)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={totalRowSX}>
              <TableCell>Total Operating Expenses</TableCell>
              <TableCell>{formatCurrencyInt(borrowerTotalOpex)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(borrowerTotalOpex, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(borrowerTotalOpex, residentialUnits))}</TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(underwriterTotalOpex)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(underwriterTotalOpex, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(underwriterTotalOpex, residentialUnits))}</TableCell>
            </TableRow>
            <TableRow sx={totalRowSX}>
              <TableCell>Total Operating Expenses Excluding Real Estate Taxes</TableCell>
              <TableCell>{formatCurrencyInt(borrowerTotalOpexExcludingTaxes)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(borrowerTotalOpexExcludingTaxes, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(borrowerTotalOpexExcludingTaxes, residentialUnits))}</TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(underwriterTotalOpexExcludingTaxes)}</TableCell>
              <TableCell>
                {formatCurrencyFloat(divide(underwriterTotalOpexExcludingTaxes, grossBuildingArea))}
              </TableCell>
              <TableCell>{formatCurrencyInt(divide(underwriterTotalOpexExcludingTaxes, residentialUnits))}</TableCell>
            </TableRow>
            <TableRow sx={totalRowSX}>
              <TableCell>Net Operating Income</TableCell>
              <TableCell>{formatCurrencyInt(borrowerNOI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(borrowerNOI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(borrowerNOI, residentialUnits))}</TableCell>
              <TableCell></TableCell>
              <TableCell>{formatCurrencyInt(underwriterNOI)}</TableCell>
              <TableCell>{formatCurrencyFloat(divide(underwriterNOI, grossBuildingArea))}</TableCell>
              <TableCell>{formatCurrencyInt(divide(underwriterNOI, residentialUnits))}</TableCell>
            </TableRow>
            <TableRow sx={totalRowSX}>
              <TableCell>Operating Expense Ratio</TableCell>
              <TableCell>{formatPercentageString(borrowerOpex, 2)}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>{formatPercentageString(underwriterOpex, 2)}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Grid>
    </Paper>
  );
});

export { ProFormaTable };
